import { useEffect, useState } from "react";
import { LineItemSearchResult, xactimateBadgeFromSearchData } from "@rebuild/shared";
import { useLineItemTemplateSearch } from "../../../hooks/useLineItemTemplateSearch";

interface LineItemPermutationSearchProps {
  value?: LineItemSearchResult;
  onSelect: (value: LineItemSearchResult | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
}

const permutationLabel = (row: LineItemSearchResult): string => row.acceptedLineString ?? row.originalLineItem;

const rowKey = (row: LineItemSearchResult): string => `${row.id}|${row.acceptedLineString ?? ""}|${row.xactCat ?? ""}|${row.xactSel ?? ""}`;

/**
 * Searchable dropdown for picking a line item permutation. The typed query is searched against the backend line item
 * permutation endpoint (via {@link useLineItemTemplateSearch}); each result row shows the permutation on the left and
 * its xactimate cat/sel badge on the right, matching the web estimate line item search.
 */
export const LineItemPermutationSearch: React.FC<LineItemPermutationSearchProps> = ({
  value,
  onSelect,
  disabled,
  placeholder = "search line item permutations",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // Only search while the dropdown is open. Selecting a template closes the dropdown and resets the
  // search term to the selected label, which would otherwise retrigger a search for the picked item.
  const { results, isLoading } = useLineItemTemplateSearch(isOpen ? searchTerm : "");

  useEffect(() => {
    setSearchTerm(value ? permutationLabel(value) : "");
  }, [value]);

  const handleSelect = (row: LineItemSearchResult) => {
    onSelect(row);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        disabled={disabled}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => {
          setSearchTerm("");
          setIsOpen(true);
        }}
        onBlur={() => {
          setSearchTerm(value ? permutationLabel(value) : "");
          setIsOpen(false);
        }}
        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}
      />
      {isLoading ? (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin"
          role="status"
          aria-label="Loading"
        />
      ) : (
        (value || searchTerm) && (
          <button
            disabled={disabled}
            onClick={() => onSelect(undefined)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        )
      )}
      {isOpen && results.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((row) => {
            const badge = xactimateBadgeFromSearchData(row);
            return (
              <button
                key={rowKey(row)}
                onMouseDown={() => handleSelect(row)}
                className="w-full flex items-start justify-between gap-2 text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                <span className="text-left">{permutationLabel(row)}</span>
                {badge ? (
                  <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-600">{badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
